import { format } from 'date-fns';
import { Task } from '../../../../../types/task';

/**
 * 任务项上下文类型
 */
export type TaskItemContext = 'normal' | 'chat';

/**
 * TaskItem共享工具类
 * 提取TaskItem和FocusTaskItem的共同逻辑，避免重复代码
 */
export class TaskItemShared {
  /**
   * 获取任务背景色类名
   * 支持上下文感知，chat上下文下使用特殊样式
   */
  static getBackgroundColorClass(context: TaskItemContext = 'normal'): string {
    if (context === 'chat') {
      return TaskItemShared.getChatBackgroundColorClass();
    }
    return 'bg-white dark:bg-ios-dark-gray5';
  }

  /**
   * 获取Chat上下文下的任务背景色类名
   * 使用透明度和模糊效果
   * 注意：这里使用固定的50%透明度，实际的动态透明度通过内联样式在组件中应用
   */
  static getChatBackgroundColorClass(): string {
    // 使用固定的50%透明度 + backdrop blur 5px
    // 动态透明度将通过内联样式在TaskItem组件中应用
    return 'bg-white/50 dark:bg-ios-dark-gray5/50 backdrop-blur-[5px]';
  }

  /**
   * 获取复选框颜色类名
   * 根据任务状态和优先级返回对应的颜色
   */
  static getCheckboxColorClass(task: Task): string {
    if (task.done) return 'border-ios-gray3 bg-ios-gray3 dark:border-ios-dark-gray3 dark:bg-ios-dark-gray3';
    if (task.vital && task.urgent) return 'border-ios-red dark:border-ios-dark-red';
    if (task.vital) return 'border-ios-orange dark:border-ios-dark-orange';
    if (task.urgent) return 'border-ios-blue dark:border-ios-dark-blue';
    return 'border-ios-gray3 dark:border-ios-dark-gray3';
  }

  /**
   * 获取边框样式类名
   * 根据是否为最后一项决定是否显示底部边框
   */
  static getBorderStyleClass(isLast: boolean): string {
    if (isLast) return 'border-b-0';
    return 'border-b border-ios-gray5 dark:border-ios-dark-gray4';
  }

  /**
   * 创建日期格式化函数
   * 返回一个格式化日期的函数，使用统一的日期格式
   */
  static createFormatDate(t: (key: string) => string) {
    return (date: string | null): string | null => {
      if (!date) return null;
      return format(new Date(date), t('common:dateFormat.shortDateTime'));
    };
  }

  /**
   * 创建循环频率格式化函数
   * 返回一个格式化RRULE的函数，支持不同的翻译命名空间
   */
  static createFormatRecurrenceFrequency(t: (key: string) => string, namespace: string) {
    return (rrule: string | null): string | null => {
      if (!rrule) return null;

      // 简单解析RRULE，提取频率
      if (rrule.includes('FREQ=DAILY')) return t(`${namespace}:recurrence.daily`);
      if (rrule.includes('FREQ=WEEKLY')) {
        if (rrule.includes('BYDAY=MO,TU,WE,TH,FR')) return t(`${namespace}:recurrence.weekdays`);
        return t(`${namespace}:recurrence.weekly`);
      }
      if (rrule.includes('FREQ=MONTHLY')) return t(`${namespace}:recurrence.monthly`);
      return t(`${namespace}:recurrence.custom`);
    };
  }

  /**
   * 创建React.memo比较函数
   * 统一的任务项比较逻辑，用于性能优化
   */
  static createMemoComparison() {
    return (prevProps: any, nextProps: any): boolean => {
      // 自定义比较函数，只在关键属性变化时重新渲染
      return (
        prevProps.task.id === nextProps.task.id &&
        prevProps.task.title === nextProps.task.title &&
        prevProps.task.done === nextProps.task.done &&
        prevProps.task.vital === nextProps.task.vital &&
        prevProps.task.urgent === nextProps.task.urgent &&
        prevProps.task.startAt === nextProps.task.startAt &&
        prevProps.task.dueAt === nextProps.task.dueAt &&
        prevProps.task.cost === nextProps.task.cost &&
        prevProps.task.notes === nextProps.task.notes &&
        prevProps.task.projectId === nextProps.task.projectId &&
        prevProps.task.project?.title === nextProps.task.project?.title &&
        prevProps.task.recurrenceRule === nextProps.task.recurrenceRule &&
        prevProps.task.isHabit === nextProps.task.isHabit &&
        prevProps.isFirst === nextProps.isFirst &&
        prevProps.isLast === nextProps.isLast &&
        prevProps.hideProjectName === nextProps.hideProjectName &&
        prevProps.onToggle === nextProps.onToggle &&
        prevProps.onClick === nextProps.onClick &&
        prevProps.onProjectClick === nextProps.onProjectClick
      );
    };
  }

  /**
   * 创建基础事件处理器工厂
   * 提供通用的事件处理逻辑，可以被不同组件扩展
   */
  static createEventHandlers(
    task: Task,
    onToggle: (taskId: string, done: boolean) => void,
    onClick: (taskId: string) => void,
    showUndoToast: (data: any) => void,
    trackEvent: (event: string, data: any) => void,
    additionalCheckboxLogic?: () => void
  ) {
    const handleCheckboxClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      const newDoneState = !task.done;
      const previousState = task.done;

      // 记录任务状态切换事件
      trackEvent(newDoneState ? 'task completed' : 'task uncompleted', {
        task_id: task.id,
        task_title: task.title.substring(0, 50), // 只记录前50个字符
        has_due_date: !!task.dueAt,
        has_project: !!task.projectId,
        is_vital: !!task.vital,
        is_urgent: !!task.urgent
      });

      // 执行额外的复选框逻辑（如专注模式的播放状态清理）
      if (additionalCheckboxLogic) {
        additionalCheckboxLogic();
      }

      // 显示撤销 toast
      showUndoToast({
        taskId: task.id,
        taskTitle: task.title,
        isCompleted: newDoneState,
        previousState: previousState
      });

      onToggle(task.id, newDoneState);
    };

    const handleItemClick = () => {
      // 记录任务详情查看事件
      trackEvent('task detail viewed', {
        task_id: task.id,
        task_title: task.title.substring(0, 50), // 只记录前50个字符
        is_completed: task.done
      });

      onClick(task.id);
    };

    return {
      handleCheckboxClick,
      handleItemClick
    };
  }
}
